import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2, CheckCircle2, XCircle, Sparkles, BookOpen, Calendar, Edit3,
    TrendingUp, Plus, X, Wand2, Clock,
} from "lucide-react";
import { tutorApi, formatApiError } from "../lib/tutorApi";
import TutorMobileLayout, { MobileSkeletonCard } from "../components/tutor/TutorMobileLayout";

/**
 * V25 Phase 5 — Tutor Attendance (mobile-first).
 * Route: /tutor/tuitions/:id/attendance
 *
 * Features:
 *  - Monthly summary bar (present / absent / pct)
 *  - Auto-generate this week's sessions button
 *  - Add manual session
 *  - List of records (descending date) with status pill + tap-to-edit
 *  - Mark today FAB
 */
const STATUS_TONE = {
    present: "bg-emerald-100 text-emerald-700 border-emerald-300",
    absent: "bg-rose-100 text-rose-700 border-rose-300",
    cancelled: "bg-slate-100 text-slate-700 border-slate-300",
    makeup: "bg-blue-100 text-blue-700 border-blue-300",
    pending: "bg-amber-100 text-amber-700 border-amber-300",
};

export default function TutorAttendance() {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [editing, setEditing] = useState(null);   // existing record being edited
    const [adding, setAdding] = useState(false);    // new manual session
    const [busy, setBusy] = useState("");
    const [month, setMonth] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await tutorApi.get(`/tutor/me/ongoing-tuitions/${id}/attendance?month=${month}`);
            setData(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setLoading(false); }
    }, [id, month]);

    useEffect(() => { load(); }, [load]);

    const generateWeek = async () => {
        setBusy("gen");
        try {
            const { data } = await tutorApi.post(`/tutor/me/ongoing-tuitions/${id}/attendance/generate-week`, {});
            if (data.created === 0) {
                toast.info("This week's sessions are already added.");
            } else {
                toast.success(`Added ${data.created} session${data.created > 1 ? "s" : ""}`);
            }
            await load();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setBusy(""); }
    };

    const records = data?.records || [];
    const summary = data?.summary || {};

    return (
        <TutorMobileLayout title="Attendance" backTo="/tutor/tuitions"
            action={
                <button onClick={generateWeek} disabled={busy === "gen"} data-testid="tutor-attendance-generate-week"
                    className="bg-white/15 hover:bg-white/25 backdrop-blur rounded-full p-2 active:scale-95 transition" title="Auto-generate this week">
                    {busy === "gen" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                </button>
            }
        >
            {loading || !data ? (
                <div className="space-y-3">
                    <MobileSkeletonCard />
                    <MobileSkeletonCard />
                </div>
            ) : (
                <>
                    {/* Header card */}
                    <div className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-2xl p-4 mb-4 shadow-md" data-testid="tutor-attendance-header">
                        <div className="text-[10px] uppercase tracking-[0.2em] opacity-80">Attendance</div>
                        <h2 className="font-bold text-lg leading-tight mt-1">{data.student_name}</h2>
                        <div className="text-xs opacity-90 mt-1">{data.schedule || "No schedule set"}</div>
                    </div>

                    {/* Month picker + summary */}
                    <div className="flex items-center gap-2 mb-3">
                        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                            data-testid="tutor-attendance-month"
                            className="flex-1 bg-white border border-slate-300 rounded-xl px-3 py-2 text-sm font-medium" />
                        <button onClick={() => setAdding(true)} data-testid="tutor-attendance-add-btn"
                            className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white rounded-xl p-2.5 active:scale-95 transition">
                            <Plus className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="grid grid-cols-4 gap-2 mb-4" data-testid="tutor-attendance-summary">
                        <SummaryPill label="Total" value={summary.total ?? 0} tone="text-slate-700 bg-slate-100" />
                        <SummaryPill label="Present" value={summary.present ?? 0} tone="text-emerald-700 bg-emerald-50" />
                        <SummaryPill label="Absent" value={summary.absent ?? 0} tone="text-rose-700 bg-rose-50" />
                        <SummaryPill label="%" value={summary.pct == null ? "—" : `${summary.pct}%`} tone="text-violet-700 bg-violet-50" />
                    </div>

                    {/* Records list */}
                    {records.length === 0 ? (
                        <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-6 text-center" data-testid="tutor-attendance-empty">
                            <Calendar className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                            <div className="font-semibold text-slate-700 text-sm">No sessions this month</div>
                            <p className="text-xs text-slate-500 mt-1 mb-3">
                                Use the wand button to auto-generate this week from your schedule, or add a session manually.
                            </p>
                            <button onClick={generateWeek} disabled={busy === "gen"} data-testid="tutor-attendance-generate-cta"
                                className="text-xs font-bold bg-gradient-to-r from-indigo-500 to-purple-600 hover:brightness-105 text-white px-4 py-2 rounded-xl inline-flex items-center gap-1.5 active:scale-95 transition disabled:opacity-60">
                                <Wand2 className="w-3.5 h-3.5" /> Auto-generate this week
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-2" data-testid="tutor-attendance-list">
                            {records.map((r) => (
                                <RecordCard key={r.id} record={r} onEdit={() => setEditing(r)} />
                            ))}
                        </div>
                    )}

                    {/* Modals */}
                    {editing && (
                        <AttendanceModal record={editing} ongoingId={id}
                            onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
                    )}
                    {adding && (
                        <AttendanceModal record={null} ongoingId={id}
                            onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />
                    )}
                </>
            )}
        </TutorMobileLayout>
    );
}

function SummaryPill({ label, value, tone }) {
    return (
        <div className={`rounded-xl px-2 py-2 text-center ${tone}`}>
            <div className="text-base font-bold leading-none">{value}</div>
            <div className="text-[10px] uppercase tracking-wider opacity-80 mt-0.5">{label}</div>
        </div>
    );
}

function RecordCard({ record, onEdit }) {
    const tone = STATUS_TONE[record.status] || STATUS_TONE.pending;
    const d = new Date(record.date);
    const dateLabel = d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
    return (
        <button onClick={onEdit} data-testid={`tutor-attendance-record-${record.id}`}
            className="w-full bg-white border border-slate-200 rounded-2xl p-3 flex items-center gap-3 hover:shadow active:scale-[0.98] transition text-left">
            <div className={`px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border ${tone}`}>
                {record.status}
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-[#0F172A] leading-tight">{dateLabel}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2">
                    {record.time_slot && <span className="inline-flex items-center gap-0.5"><Clock className="w-3 h-3" /> {record.time_slot}</span>}
                    {record.topics_covered && <span className="truncate">· {record.topics_covered.slice(0, 30)}</span>}
                </div>
                {record.edited_by_admin && (
                    <span className="text-[9px] text-amber-700 font-semibold mt-0.5 inline-block">ADMIN OVERRIDE</span>
                )}
            </div>
            <Edit3 className="w-4 h-4 text-slate-400" />
        </button>
    );
}

function AttendanceModal({ record, ongoingId, onClose, onSaved }) {
    const isEditing = !!record;
    const isAdminLocked = record?.edited_by_admin;
    const [form, setForm] = useState({
        date: record?.date || new Date().toISOString().slice(0, 10),
        status: record?.status === "pending" ? "present" : (record?.status || "present"),
        time_slot: record?.time_slot || "",
        duration_minutes: record?.duration_minutes || 60,
        topics_covered: record?.topics_covered || "",
        homework_assigned: record?.homework_assigned || "",
        homework_status: record?.homework_status || "",
        notes_tutor: record?.notes_tutor || "",
    });
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (isAdminLocked) {
            toast.error("This record was overridden by admin and cannot be edited.");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                date: form.date,
                status: form.status,
                time_slot: form.time_slot || null,
                duration_minutes: parseInt(form.duration_minutes, 10) || null,
                topics_covered: form.topics_covered || null,
                homework_assigned: form.homework_assigned || null,
                homework_status: form.homework_status || null,
                notes_tutor: form.notes_tutor || null,
            };
            if (isEditing) {
                await tutorApi.patch(`/tutor/me/ongoing-tuitions/${ongoingId}/attendance/${record.id}`, payload);
                toast.success("Updated");
            } else {
                await tutorApi.post(`/tutor/me/ongoing-tuitions/${ongoingId}/attendance`, payload);
                toast.success("Attendance marked");
            }
            onSaved();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || err.message);
        } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center" data-testid="tutor-attendance-modal">
            <div className="bg-white rounded-t-3xl w-full max-w-md max-h-[90vh] overflow-y-auto pb-6">
                <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between z-10">
                    <h3 className="font-bold text-[#0F172A]">{isEditing ? "Edit session" : "Add session"}</h3>
                    <button onClick={onClose} data-testid="tutor-attendance-modal-close"><X className="w-5 h-5 text-slate-400" /></button>
                </div>

                {isAdminLocked && (
                    <div className="mx-4 mt-3 bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800">
                        This session was edited by admin and is locked for tutor edits.
                    </div>
                )}

                <div className="px-4 py-4 space-y-3">
                    <Field label="Date" required>
                        <input type="date" value={form.date} disabled={isEditing}
                            onChange={(e) => setForm({ ...form, date: e.target.value })}
                            data-testid="tutor-attendance-date" className={inputCls} />
                    </Field>

                    <Field label="Status" required>
                        <div className="grid grid-cols-4 gap-2">
                            {["present", "absent", "cancelled", "makeup"].map((s) => (
                                <button key={s} type="button" onClick={() => setForm({ ...form, status: s })}
                                    data-testid={`tutor-attendance-status-${s}`}
                                    className={`px-2 py-2 rounded-xl text-xs font-bold uppercase tracking-wider border-2 transition ${form.status === s ? STATUS_TONE[s].replace("border-", "border-").replace("text-", "text-") : "border-slate-200 text-slate-500 bg-white"}`}>
                                    {s}
                                </button>
                            ))}
                        </div>
                    </Field>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Time">
                            <input value={form.time_slot} onChange={(e) => setForm({ ...form, time_slot: e.target.value })}
                                data-testid="tutor-attendance-time" placeholder="5-6 PM" className={inputCls} />
                        </Field>
                        <Field label="Duration (min)">
                            <input type="number" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })}
                                data-testid="tutor-attendance-duration" className={inputCls} />
                        </Field>
                    </div>

                    <Field label="Topics covered">
                        <textarea value={form.topics_covered} onChange={(e) => setForm({ ...form, topics_covered: e.target.value })}
                            data-testid="tutor-attendance-topics" rows={2} placeholder="Algebra · Linear equations…"
                            className={inputCls} />
                    </Field>

                    <Field label="Homework assigned">
                        <textarea value={form.homework_assigned} onChange={(e) => setForm({ ...form, homework_assigned: e.target.value })}
                            data-testid="tutor-attendance-homework" rows={2} placeholder="Page 45 Q1-10…"
                            className={inputCls} />
                    </Field>

                    {form.homework_assigned && (
                        <Field label="Homework status">
                            <select value={form.homework_status} onChange={(e) => setForm({ ...form, homework_status: e.target.value })}
                                data-testid="tutor-attendance-homework-status" className={inputCls}>
                                <option value="">—</option>
                                <option value="assigned">Just assigned</option>
                                <option value="pending">Pending</option>
                                <option value="done">Done</option>
                            </select>
                        </Field>
                    )}

                    <Field label="Notes (private)">
                        <textarea value={form.notes_tutor} onChange={(e) => setForm({ ...form, notes_tutor: e.target.value })}
                            data-testid="tutor-attendance-notes" rows={2} className={inputCls} />
                    </Field>

                    <button onClick={submit} disabled={busy || isAdminLocked} data-testid="tutor-attendance-submit"
                        className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:brightness-105 text-white font-bold py-3 rounded-xl mt-2 disabled:opacity-60 inline-flex items-center justify-center gap-2 active:scale-[0.98] transition">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                        {isEditing ? "Save changes" : "Mark attendance"}
                    </button>
                </div>
            </div>
        </div>
    );
}

const inputCls = "w-full border border-slate-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500";

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider mb-1 block">
                {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
            </span>
            {children}
        </label>
    );
}
